/**
 * Shared finance types used across document features (invoices, estimates, purchase orders).
 * Canonical source — feature type files should re-export from here.
 */

export type PaymentMethod = "Cash" | "Apple Pay" | "Cash App" | "Zelle" | "Crypto" | "Other";

export const PAYMENT_METHODS: readonly PaymentMethod[] = [
  "Cash",
  "Apple Pay",
  "Cash App",
  "Zelle",
  "Crypto",
  "Other",
] as const;

/** Narrows arbitrary strings to the shared payment-method union. */
export function isPaymentMethod(value: string): value is PaymentMethod {
  return PAYMENT_METHODS.some((method) => method === value);
}

export type PaymentStatus = "draft" | "unpaid" | "partial" | "paid" | "overdue";

export type ProductKanbanStatus =
  | "ordered"
  | "in_transit"
  | "received"
  | "quality_check"
  | "available"
  | "sold";

export interface SharedAccount {
  balance: number | null;
  created_at: string | null;
  customer_balance: number | null;
  id: string;
  is_customer: boolean | null;
  is_vendor: boolean | null;
  name: string | null;
  photo: string | null;
  type: string | null;
  uid: string;
  vendor_balance: number | null;
}

export interface SharedProduct {
  account_id: string | null;
  category: string | null;
  cost: number | null;
  created_at: string | null;
  id: string;
  is_deleted: boolean;
  is_fronted: boolean | null;
  is_miscellaneous?: boolean | null;
  is_sample: boolean | null;
  is_service?: boolean | null;
  kanban_status: ProductKanbanStatus | null;
  notes?: string | null;
  photo_url: string | null;
  product_name: string | null;
  purchase_order_id: string | null;
  purchase_order_uid: string | null;
  quantity: number | null;
  sku: string | null;
  total_cost: number | null;
  total_units_behind_sample?: number | null;
  uid: string | null;
  vendor_product_name: string | null;
}

/**
 * Editable product columns exposed to operators. Every field is optional: only
 * the keys present in a payload are written, so an omitted key keeps its stored
 * value while an explicit `null` clears a nullable column. A payload carrying
 * no editable key is rejected by `buildProductUpdates` in `src/lib/db/products.ts`.
 */
export interface SharedUpdateProductPayload {
  category?: string | null;
  cost?: number;
  is_fronted?: boolean;
  is_miscellaneous?: boolean;
  is_sample?: boolean;
  is_service?: boolean;
  kanban_status?: ProductKanbanStatus | null;
  notes?: string | null;
  product_name?: string | null;
  quantity?: number;
  total_units_behind_sample?: number | null;
  vendor_product_name?: string;
}

/**
 * In-progress payment captured while a document is being created.
 *
 * Deliberately string-based: the operator is mid-edit, so amounts stay exactly
 * as typed until the create payload is assembled. `enabled: false` means no
 * payment row is written at all.
 */
export interface PaymentDraft {
  amount: string;
  date: string;
  enabled: boolean;
  method: PaymentMethod;
  reference: string;
}

export interface DocumentFinancials {
  balance: number | null;
  payment_status: PaymentStatus | null;
  total_amount: number | null;
  total_paid: number | null;
}
